/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import MsDyn365, { IComponent, IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import React from 'react';

import { QuickOrderProduct } from '../quick-order-product';
import { IQuickOrderTableHeaderResources, QuickOrderTableHeaderComponent } from './components/quick-order-table-header-component';
import { IQuickOrderTableRowResources, QuickOrderTableRowComponent } from './components/quick-order-table-row-component';

/**
 * Quick order table data interface.
 */
interface IQuickOrderTableData {
    quickOrderProducts: QuickOrderProduct[];
}

/**
 * Quick order table resources.
 */
export interface IQuickOrderTableResources extends IQuickOrderTableHeaderResources, IQuickOrderTableRowResources {}

/**
 * Quick order table props.
 */
export interface IQuickOrderTableProps extends IComponentProps<IQuickOrderTableData> {
    context: ICoreContext;
    className: string;

    resources: IQuickOrderTableResources;
    isMobileView: boolean;
}

/**
 * Quick order table component.
 */
export interface IQuickOrderTableComponent extends IComponent<IQuickOrderTableProps> {}

/**
 * The quick order table.
 * @param props -- The quick order table props.
 * @returns The quick order table element.
 */
export const QuickOrderTableComponent: React.FC<IQuickOrderTableProps> = (props: IQuickOrderTableProps) => {
    const [isMobile, setIsMobile] = React.useState(props.isMobileView);
    const quickOrderProducts = props.data.quickOrderProducts;

    /**
     * Update the view port.
     */
    const updateViewport = () => {
        setIsMobile(props.isMobileView);
    };
    if (MsDyn365.isBrowser) {
        window.addEventListener('resize', updateViewport);
    }

    const headerClassName = `${props.className}__header`;
    const rowClassName = `${props.className}__row`;

    return (
        <table className={props.className}>
            <QuickOrderTableHeaderComponent
                context={props.context}
                typeName={props.typeName}
                id={headerClassName}
                className={headerClassName}
                isMobile={isMobile}
                resources={props.resources}
                data={{}}
            />
            {quickOrderProducts.map((quickOrderProduct, index) => {
                return (
                    <QuickOrderTableRowComponent
                        context={props.context}
                        typeName={props.typeName}
                        id={`${rowClassName}-${index}`}
                        key={quickOrderProduct.simpleProduct.ItemId}
                        className={rowClassName}
                        resources={props.resources}
                        isMobile={isMobile}
                        data={{
                            cartLine: quickOrderProduct.cartLine,
                            product: quickOrderProduct.simpleProduct
                        }}
                    />
                );
            })}
        </table>
    );
};
